import {
  getCanonicalProvinceCodesSortedAlphabeticallyByDisplayName,
  provName,
  toProv,
} from "../../components/Analytics/PriceReports/provinceUtils";
import { chartJsCategoryAxisTicksWithWordWrap } from "../pricing-reports/chartJsCategoryAxisTickWordWrap";
import { pricingLineChartOptions } from "../pricing-reports/ChartBlock";
import { getCountFromBeefAiAggregateRow } from "../pricing-reports/productSnapshotChartUtils";
import {
  CHART_COLORS,
  compareMonthKeys,
  INSIGHTS_BAR_CHART_MAX_BARS,
  limitOrderedCategories,
  monthKeyFromRow,
  resolveInsightsBarChartColor,
} from "../pricing-reports/pricingChartsUtils";

const flyerBarChartCategoryAxis = {
  layout: {
    padding: {
      bottom: 8,
    },
  },
  scales: {
    x: {
      ticks: chartJsCategoryAxisTicksWithWordWrap(),
    },
  },
};

/** First three palette entries — aligned with [`pricingChartsUtils.js`](../pricing-reports/pricingChartsUtils.js). */
export const FLYER_DISCOUNT_STACK_COLORS = CHART_COLORS.slice(0, 3);

export const FLYER_DISCOUNT_BUCKET_LABELS = {
  percentOff: "% off",
  dollarOff: "Dollars off",
  regularSale: "Regular price",
};

const FLYER_DISCOUNT_BUCKET_ORDER = ["regularSale", "percentOff", "dollarOff"];

const FLYER_DISCOUNT_COLOR_BY_BUCKET = {
  percentOff: FLYER_DISCOUNT_STACK_COLORS[0],
  dollarOff: FLYER_DISCOUNT_STACK_COLORS[1],
  regularSale: FLYER_DISCOUNT_STACK_COLORS[2],
};

/**
 * Bucket rows by `sale_type` (amount | percent); all other rows are Regular price.
 *
 * @param {object} row
 * @returns {'percentOff' | 'dollarOff' | 'regularSale'}
 */
export function mapRowToFlyerDiscountBucket(row) {
  const saleType = String(row?.sale_type ?? "").trim();
  if (saleType === "percent") return "percentOff";
  if (saleType === "amount") return "dollarOff";
  return "regularSale";
}

/**
 * @param {object[]} rows — aggregate rows from `fetchFlyerOfferCountByRetailerAndDiscountType`
 */
export function buildFlyerRetailerStackedBarChart(rows = []) {
  const countByRetailerAndBucket = new Map();

  for (const row of rows) {
    const retailer = String(row?.retailer ?? "").trim();
    if (!retailer) continue;
    const bucket = mapRowToFlyerDiscountBucket(row);
    const count = getCountFromBeefAiAggregateRow(row);
    const key = `${retailer}\0${bucket}`;
    countByRetailerAndBucket.set(key, (countByRetailerAndBucket.get(key) || 0) + count);
  }

  const retailerTotals = new Map();
  for (const [key, count] of countByRetailerAndBucket.entries()) {
    const [retailer] = key.split("\0");
    retailerTotals.set(retailer, (retailerTotals.get(retailer) || 0) + count);
  }

  const retailersSorted = limitOrderedCategories(
    [...retailerTotals.entries()]
      .sort((a, b) => {
        if (b[1] !== a[1]) return b[1] - a[1];
        return a[0].localeCompare(b[0]);
      })
      .map(([retailer]) => retailer),
    INSIGHTS_BAR_CHART_MAX_BARS
  );

  const datasets = FLYER_DISCOUNT_BUCKET_ORDER.map((bucketKey) => ({
    label: FLYER_DISCOUNT_BUCKET_LABELS[bucketKey],
    data: retailersSorted.map((retailer) => {
      return countByRetailerAndBucket.get(`${retailer}\0${bucketKey}`) || 0;
    }),
    backgroundColor: FLYER_DISCOUNT_COLOR_BY_BUCKET[bucketKey],
    stack: "offers",
  }));

  const retailerChartHasAnyData = datasets.some((dataset) =>
    dataset.data.some((value) => value > 0)
  );

  return {
    chart: {
      labels: retailersSorted,
      datasets,
    },
    retailerChartHasAnyData,
  };
}

export const flyerLineChartOptions = {
  ...pricingLineChartOptions,
  plugins: {
    ...pricingLineChartOptions.plugins,
    tooltip: {
      callbacks: {
        label: (context) => {
          const promotionCount = typeof context.parsed?.y === "number" ? context.parsed.y : 0;
          return `${context.dataset.label}: ${promotionCount} promotion${promotionCount === 1 ? "" : "s"}`;
        },
      },
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      title: { display: true, text: "Promotion count" },
      ticks: { precision: 0 },
    },
  },
};

/**
 * @param {object[]} rows — raw monthly rows from `fetchFlyerPromotionalFrequency`
 */
export function buildFlyerPromotionalFrequencyLineChart(rows = []) {
  const countByMonth = new Map();

  for (const row of rows) {
    const monthKey = monthKeyFromRow(row);
    if (!monthKey) continue;
    const count = getCountFromBeefAiAggregateRow(row);
    countByMonth.set(monthKey, (countByMonth.get(monthKey) || 0) + count);
  }

  const labels = [...countByMonth.keys()].sort(compareMonthKeys);
  const data = labels.map((monthKey) => countByMonth.get(monthKey) || 0);
  const frequencyLineHasAnyData = data.some((value) => value > 0);

  return {
    chart: {
      labels,
      datasets: [
        {
          label: "Promotions",
          data,
          borderColor: resolveInsightsBarChartColor(),
          backgroundColor: resolveInsightsBarChartColor(),
          tension: 0.2,
        },
      ],
    },
    frequencyLineHasAnyData,
  };
}

export const flyerProvinceBarChartOptions = {
  ...flyerBarChartCategoryAxis,
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: true, position: "bottom" },
    tooltip: {
      callbacks: {
        label: (context) => {
          const promotionCount = typeof context.parsed?.y === "number" ? context.parsed.y : 0;
          return `${context.dataset.label}: ${promotionCount} promotion${promotionCount === 1 ? "" : "s"}`;
        },
      },
    },
  },
  scales: {
    ...flyerBarChartCategoryAxis.scales,
    y: {
      beginAtZero: true,
      title: { display: true, text: "Promotion count" },
      ticks: { precision: 0 },
    },
  },
};

export const stackedBarChartOptions = {
  ...flyerBarChartCategoryAxis,
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: "index", intersect: false },
  plugins: {
    legend: { display: true, position: "bottom" },
    tooltip: {
      callbacks: {
        label: (context) => {
          const promotionCount = typeof context.parsed?.y === "number" ? context.parsed.y : 0;
          return `${context.dataset.label}: ${promotionCount} promotion${promotionCount === 1 ? "" : "s"}`;
        },
      },
    },
  },
  scales: {
    x: {
      ...flyerBarChartCategoryAxis.scales.x,
      stacked: true,
    },
    y: {
      stacked: true,
      beginAtZero: true,
      title: { display: true, text: "Promotion count" },
      ticks: { precision: 0 },
    },
  },
};

function compareProvinceCodesAlphabeticallyByDisplayName(codeA, codeB) {
  return provName(codeA).localeCompare(provName(codeB));
}

/**
 * @param {string[]} provinceCodes
 * @param {Record<string, number>} offerCountByProvinceCode
 */
function sortProvinceCodesByOfferCountHighToLow(provinceCodes, offerCountByProvinceCode) {
  const withData = [];
  const withoutData = [];

  for (const code of provinceCodes) {
    const count = offerCountByProvinceCode[code] || 0;
    if (count > 0) {
      withData.push(code);
    } else {
      withoutData.push(code);
    }
  }

  withData.sort((codeA, codeB) => {
    const countDelta =
      (offerCountByProvinceCode[codeB] || 0) - (offerCountByProvinceCode[codeA] || 0);
    if (countDelta !== 0) return countDelta;
    return compareProvinceCodesAlphabeticallyByDisplayName(codeA, codeB);
  });

  withoutData.sort(compareProvinceCodesAlphabeticallyByDisplayName);

  return [...withData, ...withoutData];
}

/**
 * @param {object[]} provinceRows — aggregate rows from `fetchFlyerOfferCountByProvince`
 */
export function buildFlyerProvinceOfferCountChart(provinceRows = []) {
  const offerCountByProvinceCode = {};

  for (const row of provinceRows) {
    const provinceCode = toProv(row.province);
    if (!provinceCode) continue;
    const nextCount = getCountFromBeefAiAggregateRow(row);
    const previousCount = offerCountByProvinceCode[provinceCode] || 0;
    if (nextCount >= previousCount) {
      offerCountByProvinceCode[provinceCode] = nextCount;
    }
  }

  const canonicalProvinceCodes = getCanonicalProvinceCodesSortedAlphabeticallyByDisplayName();
  const orderedProvinceCodes = limitOrderedCategories(
    sortProvinceCodesByOfferCountHighToLow(canonicalProvinceCodes, offerCountByProvinceCode),
    INSIGHTS_BAR_CHART_MAX_BARS
  );
  const labels = orderedProvinceCodes.map((code) => provName(code));
  const data = orderedProvinceCodes.map((code) => {
    const count = offerCountByProvinceCode[code];
    return count == null || count === 0 ? null : count;
  });
  const provinceChartHasAnyData = orderedProvinceCodes.some(
    (code) => (offerCountByProvinceCode[code] || 0) > 0
  );

  return {
    chart: {
      labels,
      datasets: [
        {
          label: "Promotions",
          data,
          backgroundColor: resolveInsightsBarChartColor(),
        },
      ],
    },
    provinceChartHasAnyData,
  };
}
